/**
 * ForgotPasswordPage — /forgot-password route (superall)
 *
 * Three-step OTP password reset:
 *   1. Phone input → POST /api/v1/auth/forgot-password (surface: "superall")
 *   2. OTP + new password → POST /api/v1/auth/reset-password
 *   3. Success → navigate to /login
 *
 * Uses superall's i18n (useT / useLang). RTL-aware.
 */

import * as React from 'react';
import { useLocation } from 'wouter';
import { CheckCircle2, Phone, ShieldCheck } from 'lucide-react';
import { FadeIn } from '@workspace/ui';
import { useT, useLang } from '../providers/LangProvider';
import { PLATFORM_API } from '../lib/api';

const SURFACE = 'superall';

function validateSaudiPhone(raw: string): string | null {
  const digits = raw.replace(/\D/g, '');
  if (/^05\d{8}$/.test(digits)) return '+966' + digits.slice(1);
  if (/^5\d{8}$/.test(digits)) return '+966' + digits;
  if (/^\+9665\d{8}$/.test(raw)) return raw;
  return null;
}

export function ForgotPasswordPage(): React.ReactElement {
  const t = useT();
  const [lang] = useLang();
  const isAr = lang === 'ar';
  const [, setLocation] = useLocation();

  const [step, setStep] = React.useState<'phone' | 'otp' | 'done'>('phone');
  const [phone, setPhone] = React.useState('');
  const [normalizedPhone, setNormalizedPhone] = React.useState('');
  const [otp, setOtp] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const API_BASE = `${PLATFORM_API}/v1/auth`;

  async function handleSendOtp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const normalized = validateSaudiPhone(phone.trim());
    if (!normalized) {
      setError(isAr ? 'رقم الجوال غير صحيح' : 'Invalid phone number');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: normalized, surface: SURFACE }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (res.status === 429) {
          const secs = res.headers.get('Retry-After') ?? '60';
          setError(isAr ? `يرجى الانتظار ${secs} ثانية` : `Please wait ${secs}s before retrying`);
          return;
        }
        setError(data?.error?.message ?? (isAr ? 'حدث خطأ' : 'An error occurred'));
        return;
      }
      setNormalizedPhone(normalized);
      setStep('otp');
    } catch {
      setError(isAr ? 'تعذّر الاتصال بالخادم' : 'Could not connect to the server');
    } finally {
      setLoading(false);
    }
  }

  async function handleReset(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (otp.length !== 6 || !/^\d{6}$/.test(otp)) {
      setError(isAr ? 'أدخل الرمز المكوّن من 6 أرقام' : 'Enter the 6-digit code');
      return;
    }
    if (newPassword.length < 8) {
      setError(
        isAr ? 'كلمة المرور يجب أن تكون 8 أحرف على الأقل' : 'Password must be at least 8 characters'
      );
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(isAr ? 'كلمتا المرور غير متطابقتين' : 'Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: normalizedPhone, surface: SURFACE, otp, newPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const code = data?.error?.code ?? '';
        const msgMap: Record<string, { en: string; ar: string }> = {
          WRONG_CODE: { en: 'Incorrect OTP', ar: 'الرمز غير صحيح' },
          EXPIRED: { en: 'OTP has expired', ar: 'انتهت صلاحية الرمز' },
          ALREADY_USED: { en: 'OTP already used', ar: 'تم استخدام الرمز مسبقاً' },
          LOCKED: { en: 'Too many attempts', ar: 'محاولات كثيرة جداً' },
        };
        const msg = msgMap[code];
        setError(msg ? (isAr ? msg.ar : msg.en) : (data?.error?.message ?? t.loginError));
        return;
      }
      setStep('done');
    } catch {
      setError(isAr ? 'تعذّر الاتصال بالخادم' : 'Could not connect to the server');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="min-h-screen bg-neutral-50 dark:bg-[#0f0a1e] font-sans"
      dir={isAr ? 'rtl' : 'ltr'}
    >
      <header className="border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-4 py-3">
        <h1
          className="text-xl font-bold text-purple-900 dark:text-purple-300"
          style={{ fontFamily: 'system-ui, sans-serif' }}
        >
          {t.title}
        </h1>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-6">
        <FadeIn delay={0.1}>
          <div className="mx-auto mt-8 max-w-sm rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-6 shadow-sm">
            {/* Icon + heading */}
            <div className="mb-5 flex flex-col items-center gap-2 text-center">
              {step === 'done' ? (
                <CheckCircle2 size={36} className="text-emerald-500" />
              ) : step === 'phone' ? (
                <Phone size={36} className="text-purple-700 dark:text-purple-400" />
              ) : (
                <ShieldCheck size={36} className="text-purple-700 dark:text-purple-400" />
              )}
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                {step === 'done' ? t.resetSuccess : t.forgotPasswordTitle}
              </h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                {step === 'phone' && t.forgotPasswordSubtitle}
                {step === 'otp' && `${t.otpSentTo} ${normalizedPhone}`}
                {step === 'done' && t.resetSuccessSubtitle}
              </p>
            </div>

            {/* Step 1: Phone */}
            {step === 'phone' && (
              <form onSubmit={handleSendOtp} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <label
                    className="text-sm font-medium text-neutral-700 dark:text-neutral-300"
                    htmlFor="fp-phone"
                  >
                    {isAr ? 'رقم الجوال' : 'Mobile Number'}
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center rounded-s-lg border border-e-0 border-neutral-300 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-800 px-3 text-sm font-mono text-neutral-500 dark:text-neutral-400">
                      +966
                    </span>
                    <input
                      id="fp-phone"
                      type="tel"
                      inputMode="numeric"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="05xxxxxxxx"
                      dir="ltr"
                      className="flex-1 rounded-e-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 px-3 py-2 text-sm outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                      autoFocus
                    />
                  </div>
                </div>
                {error && (
                  <p
                    role="alert"
                    className="rounded-lg bg-red-50 dark:bg-red-950/30 px-3 py-2 text-xs text-red-600 dark:text-red-400"
                  >
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="rounded-lg bg-purple-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-purple-800 disabled:opacity-60"
                >
                  {loading ? t.sending : t.sendOtp}
                </button>
                <button
                  type="button"
                  onClick={() => setLocation('/login')}
                  className="text-sm text-neutral-500 dark:text-neutral-400 underline underline-offset-2"
                >
                  {t.backToSignIn}
                </button>
              </form>
            )}

            {/* Step 2: OTP + new password */}
            {step === 'otp' && (
              <form onSubmit={handleReset} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <label
                    className="text-sm font-medium text-neutral-700 dark:text-neutral-300"
                    htmlFor="fp-otp"
                  >
                    {isAr ? 'رمز التحقق' : 'Verification Code'}
                  </label>
                  <input
                    id="fp-otp"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    dir="ltr"
                    className="rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 px-3 py-2 text-center text-xl font-mono tracking-widest outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                    autoFocus
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label
                    className="text-sm font-medium text-neutral-700 dark:text-neutral-300"
                    htmlFor="fp-new-pass"
                  >
                    {t.newPassword}
                  </label>
                  <input
                    id="fp-new-pass"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={isAr ? '8 أحرف على الأقل' : 'At least 8 characters'}
                    className="rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 px-3 py-2 text-sm outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label
                    className="text-sm font-medium text-neutral-700 dark:text-neutral-300"
                    htmlFor="fp-confirm-pass"
                  >
                    {t.confirmPassword}
                  </label>
                  <input
                    id="fp-confirm-pass"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={isAr ? 'أعد كتابة كلمة المرور' : 'Re-enter password'}
                    className="rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 px-3 py-2 text-sm outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                  />
                </div>
                {error && (
                  <p
                    role="alert"
                    className="rounded-lg bg-red-50 dark:bg-red-950/30 px-3 py-2 text-xs text-red-600 dark:text-red-400"
                  >
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  className="rounded-lg bg-purple-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-purple-800 disabled:opacity-60"
                >
                  {loading ? t.resetting : t.resetPassword}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStep('phone');
                    setOtp('');
                    setError(null);
                  }}
                  className="text-sm text-neutral-500 dark:text-neutral-400 underline underline-offset-2"
                >
                  {t.changePhone}
                </button>
              </form>
            )}

            {/* Step 3: Success */}
            {step === 'done' && (
              <button
                type="button"
                onClick={() => setLocation('/login')}
                className="w-full rounded-lg bg-purple-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-purple-800"
              >
                {t.signInBtn}
              </button>
            )}
          </div>
        </FadeIn>
      </main>
    </div>
  );
}
